import { deepLinks } from '@/components/deepLinks';
import { compressData, isNonNil } from '@vuddy/utils';
import { env } from 'next-runtime-env';

interface IGetRouterPathProps {
  isAppWebView?: boolean;
}

interface ChannelRouterPathParam {
  channelUrl: string;
  channelId?: number;
}

export const getRouterPath = (
  { isAppWebView }: IGetRouterPathProps = {
    isAppWebView: false,
  }
) => {
  return {
    home: isAppWebView ? deepLinks.home : '/',
    login: isAppWebView ? deepLinks.login : '/login',
    loginRedirect: (redirectUrl: string) =>
      isAppWebView ? deepLinks.login : `/login?redirectUrl=${redirectUrl}`,
    signupTerms: '/signup/terms',
    signupEmail: '/signup/email',
    signupEmailPincode: (emailAddress: string) =>
      `/signup/email/pincode?emailAddress=${emailAddress}`,
    ranking: '/ranking',
    my: '/my',
    myAccount: '/my/account',
    myAccountLeave: '/my/account/leave',
    unavailableLeave: '/my/account/leave/unavailable',
    myAccountLeavePincode: (emailAddress: string) =>
      `/my/account/leave/pincode?emailAddress=${emailAddress}`,
    myAccountLeaveComplete: '/my/account/leave/complete',
    myAccountEmail: '/my/account/email',
    classRoom: '/classroom',
    classRoomDetail: ({ classId }: { classId: number }) =>
      `/classroom/${classId}`,
    channelsChoice: '/channels/choice',
    orderList: '/orders',
    orderClaimHistory: '/orders/claims',
    gachaList: '/gachas',
    gachaDelivery: (gachaRequestId: number) =>
      `/gachas/delivery/${gachaRequestId}`,
    gachaResult: '/gachas/result',
    gachaPolling: ({
      gachaRequestId,
      userGachaTicketIds,
      fromSalePage,
      options,
    }: {
      gachaRequestId?: number;
      userGachaTicketIds?: number[];
      fromSalePage: boolean;
      options?: {
        forceWebUrl?: boolean;
      };
    }) =>
      isAppWebView && !options?.forceWebUrl
        ? deepLinks.gachaDraw({
            gachaRequestId,
            userGachaTicketIds: (userGachaTicketIds ?? [])?.join(','),
            fromSalePage,
          })
        : `/gachas/polling?${userGachaTicketIds ? `userGachaTicketIds=${compressData<number[]>(userGachaTicketIds)}` : `gachaRequestId=${gachaRequestId}`}`,
    orderDetail: (orderId: number) => `/orders/${orderId}`,
    orderClaimCancelDetail: (claimRequestId: number, orderId?: number) => {
      const base = `/orders/claims/cancel/${claimRequestId}`;
      return orderId != null ? `${base}?orderId=${orderId}` : base;
    },
    orderClaimExchangeDetail: (claimRequestId: number) =>
      `/orders/claims/exchange/${claimRequestId}`,
    orderClaimExchangeComplete: (claimRequestId: number) =>
      `/orders/claims/exchange/complete/${claimRequestId}`,
    orderClaimExchangeRequest: (orderItemId: number, orderId: number) =>
      `/orders/claims/exchange/request/${orderItemId}?orderId=${orderId}`,
    orderClaimReturnDetail: (claimRequestId: number) =>
      `/orders/claims/return/${claimRequestId}`,
    orderClaimReturnComplete: (claimRequestId: number) =>
      `/orders/claims/return/complete/${claimRequestId}`,
    orderClaimReturnRequest: (orderItemId: number, orderId: number) =>
      `/orders/claims/return/request/${orderItemId}?orderId=${orderId}`,
    channels: '/channels',
    channelByUrlName: (channelUrlName: string) =>
      `/channels/${encodeURIComponent(channelUrlName)}/home`,
    channelsHome: ({ channelUrl, channelId }: ChannelRouterPathParam) =>
      isAppWebView && channelId
        ? deepLinks.channelDetail({ channelId, tab: 'Home' })
        : `/channels/${channelUrl}/home`,
    channelsMyBox: ({ channelUrl, channelId }: ChannelRouterPathParam) =>
      isAppWebView && channelId
        ? deepLinks.channelDetail({ channelId, tab: 'MyBox' })
        : `/channels/${channelUrl}/my-box`,
    channelShop: ({ channelUrl, channelId }: ChannelRouterPathParam) =>
      isAppWebView && channelId
        ? deepLinks.channelDetail({ channelId, tab: 'Shop' })
        : `/channels/${channelUrl}/shop`,
    channelClass: ({ channelUrl }: ChannelRouterPathParam) =>
      `/channels/${channelUrl}/class`,
    channelCollection: ({ channelUrl, channelId }: ChannelRouterPathParam) =>
      isAppWebView && channelId
        ? deepLinks.channelDetail({
            channelId,
            tab: 'Collection',
          })
        : `/channels/${channelUrl}/collection`,
    channelCollectionDetail: ({
      channelUrl,
      channelId,
      collectionId,
    }: ChannelRouterPathParam & { collectionId: number }) =>
      isAppWebView && channelId
        ? deepLinks.channelDetail({
            channelId,
            tab: 'Collection',
          })
        : `/channels/${channelUrl}/collection/${collectionId}`,
    channelCollectionList: ({
      channelUrl,
      channelId,
    }: ChannelRouterPathParam) =>
      isAppWebView && channelId
        ? deepLinks.channelDetail({
            channelId,
            tab: 'Collection',
          })
        : `/channels/${channelUrl}/collections`,
    channelPostDetail: ({
      channelUrl,
      postId,
    }: {
      channelUrl: string;
      postId: number;
    }) => `/channels/${channelUrl}/post/${postId}`,
    channelPostComments: ({
      channelUrl,
      postId,
    }: {
      channelUrl: string;
      postId: number;
    }) => `/channels/${channelUrl}/post/${postId}/comments`,
    channelPostDetailAirbridge: ({
      channelId,
      channelUrl,
      postId,
    }: {
      channelId: number;
      channelUrl: string;
      postId: number;
    }) => {
      const airbridgeBaseUrl = env('NEXT_PUBLIC_AIRBRIDGE_POST_LINK');
      return `${airbridgeBaseUrl}?channel_id=${channelId}&channel_url=${encodeURIComponent(channelUrl)}&post_id=${postId}`;
    },
    channelPostDetailDeepLink: ({
      channelId,
      channelUrl,
      postId,
    }: {
      channelId: number;
      channelUrl: string;
      postId: number;
    }) =>
      `vuddy://post?channel_id=${channelId}&channel_url=${encodeURIComponent(channelUrl)}&post_id=${postId}`,
    delivery: '/delivery',
    newDelivery: '/delivery/new',
    deliveryDetail: (deliveryId: number | 'new') => `/delivery/${deliveryId}`,
    deliveryChange: (
      requestId: number,
      type: 'order' | 'claimPickup' | 'claimDelivery' = 'order'
    ) => `/delivery/change/${requestId}?type=${type}`,
    policies: '/my/policies',
    promotionCode: '/promotion-code',
    orderSheet: (
      channelId: number,
      salePageId: number,
      orderItemsStr: string,
      hasPhysical: boolean
    ) =>
      `/ordersheet/${salePageId}?channelId=${channelId}&hasPhysical=${hasPhysical}&orderItems=${orderItemsStr}`,
    orderSheetByEntry: (salePageId: number, entryId: string) =>
      `/ordersheet/${salePageId}?entryId=${encodeURIComponent(entryId)}`,
    orderDelivery: (deliveryId?: number) =>
      `/ordersheet/delivery${deliveryId ? `?deliveryId=${deliveryId}` : ''}`,
    cartDelivery: (deliveryId?: number) =>
      `/cart/delivery${deliveryId ? `?deliveryId=${deliveryId}` : ''}`,
    notification: '/notification',
    myBox: '/mybox',
    saleDetail: (salePageId: number) => `/sales/${salePageId}`,
    comments: (salePageId: number) => `/sales/${salePageId}/comments`,
    endedSale: (salePageId: number) => `/sales/${salePageId}/ended`,
    viewerDetail: (userMediaItemId: number) => `/viewer/${userMediaItemId}`,
    cardOpening: (openMediaItemIds: number[]) =>
      `/open-result?itemIds=${openMediaItemIds.join(',')}`,
    notFound: '/not-found',
    customOrderForm: (salePageId: number, orderId?: number) =>
      `/sales/${salePageId}/custom-order-form${isNonNil(orderId) ? `/${orderId}` : ''}`,
    seriesDetail: (seriesId: number) => `/series/${seriesId}`,
    voteDetail: (seriesId: number, voteId: number) =>
      `/series/${seriesId}/vote/${voteId}`,
    categoryHome: (categoryId: number) => `/categories/${categoryId}`,
    cart: '/cart',
  };
};

export const studioRouterPath = {
  channelHome: (channelId: number) =>
    `${env('NEXT_PUBLIC_STUDIO_URL')}/channel/${channelId}`,
  channelCreate: `${env('NEXT_PUBLIC_STUDIO_URL')}/channel/create`,
  channelSelect: `${env('NEXT_PUBLIC_STUDIO_URL')}/channel`,
};

export const kcpRouterPath = {
  mobileReturnUrl: `${env('NEXT_PUBLIC_WEB_URL')}/sales/kcp-result`,
  mobilePaymentResult: `${env('NEXT_PUBLIC_WEB_URL')}/sales/payment-result`,
};
